import { apiPaths } from "@/lib/api/backendUrls"
import { fetchJson } from "@/lib/api/http"

export type ApplicationConfigurationDto = {
  currentUser?: {
    id?: string | null
    isAuthenticated?: boolean
    roles?: string[]
  }
  auth?: {
    grantedPolicies?: Record<string, boolean>
  }
  extraProperties?: Record<string, unknown>
}

const asRecord = (v: unknown): Record<string, unknown> | null =>
  typeof v === "object" && v !== null ? (v as Record<string, unknown>) : null

/**
 * `auth.grantedPolicies` из GET `/api/abp/application-configuration` (Swagger: ApplicationConfigurationDto).
 * Поддержка camelCase и PascalCase в JSON (разные настройки сериализации ABP).
 */
export const readGrantedPoliciesFromApplicationConfiguration = (cfg: unknown): Record<string, boolean> => {
  const root = asRecord(cfg) ?? {}
  const auth = asRecord(root.auth ?? root.Auth) ?? {}
  const raw = auth.grantedPolicies ?? auth.GrantedPolicies
  if (raw === null || raw === undefined) return {}
  if (typeof raw !== "object" || Array.isArray(raw)) return {}
  const out: Record<string, boolean> = {}
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === "boolean") out[k] = v
  }
  return out
}

const readCurrentUser = (cfg: unknown): Record<string, unknown> | null => {
  const root = asRecord(cfg) ?? {}
  return asRecord(root.currentUser ?? root.CurrentUser)
}

/** Роли из `currentUser` / `CurrentUser` в ответе application-configuration. */
export const readCurrentUserRolesFromApplicationConfiguration = (cfg: unknown): string[] | undefined => {
  const cu = readCurrentUser(cfg)
  if (!cu) return undefined
  const roles = cu.roles ?? cu.Roles
  return Array.isArray(roles) ? (roles as string[]) : undefined
}

export const readCurrentUserIdFromApplicationConfiguration = (cfg: unknown): string | null => {
  const cu = readCurrentUser(cfg)
  if (!cu) return null
  const id = cu.id ?? cu.Id
  return typeof id === "string" && id.trim() ? id : null
}

export const getApplicationConfiguration = async (accessToken: string) => {
  const path = `${apiPaths.applicationConfiguration}?IncludeLocalizationResources=false`
  return fetchJson<ApplicationConfigurationDto>(path, {
    method: "GET",
    token: accessToken,
  })
}

/** Как в leninkaart_front: флаг из application-configuration.extraProperties / ExtraProperties */
export const readHasManageableEvents = (cfg: unknown) => {
  const root = asRecord(cfg) ?? {}
  const ep = asRecord(root.extraProperties ?? root.ExtraProperties)
  if (!ep) return false
  const raw = ep.HasManageableEvents ?? ep.hasManageableEvents
  return Boolean(raw)
}
